import { Button } from '@timelyapp/react';
import classNames from 'classnames';
import ClearableButton from 'components/buttons/ClearableButton';
import React from 'react';
import styles from './Popover.module.scss';

export const OPEN_CLASS_NAME = 'PopoverTarget_openClass';

interface Props {
  target: React.ReactElement;
  isCurrentlyOpen: boolean;
  targetCaret: boolean;
  ignoreTargetOnClick: boolean;
  onClick: (event: React.MouseEvent) => void;
  onFocus: null | ((event: React.FocusEvent) => void);
  targetWrapperElement: string;
  targetWrapperStyle?: React.CSSProperties;
  targetWrapper: boolean;
}

const BUTTON_COMPONENT_TYPES: Array<string | React.JSXElementConstructor<any>> = [
  Button,
  ClearableButton,
];

export const PopoverTarget = React.forwardRef((props: Props, ref) => {
  const {
    target,
    isCurrentlyOpen,
    targetCaret,
    ignoreTargetOnClick,
    onClick,
    onFocus = null,
    targetWrapperElement,
    targetWrapper,
  } = props;

  // Not all popovers require a target
  if (!target) {
    return null;
  }

  const targetProps = {
    ...target.props,
    onFocus,
    onClick: (event: React.MouseEvent) => {
      event.stopPropagation();

      if (!ignoreTargetOnClick) {
        target.props.onClick?.(event);
      }

      onClick(event);
    },
    className: classNames(target.props.className, { [OPEN_CLASS_NAME]: isCurrentlyOpen }),
  };

  if (BUTTON_COMPONENT_TYPES.includes(target.type)) {
    targetProps.isActive = isCurrentlyOpen;
    if (targetProps.caret !== false) {
      targetProps.caret = targetCaret;
    }
  }

  // Wrap the target in whatever element specified
  if (targetWrapper) {
    return React.createElement(
      targetWrapperElement,
      { className: styles.targetWrapper, ref, style: props.targetWrapperStyle },
      React.cloneElement(target, targetProps)
    );
  }

  targetProps.ref = ref;
  return React.cloneElement(target, targetProps);
});

PopoverTarget.displayName = 'PopoverTarget';
